import React, { useState, useEffect } from "react";
import { X, Loader2, ArrowUpCircle, ArrowDownCircle, CheckCircle, Ban, RefreshCw } from "lucide-react";
import { TransactionStatus } from "../../../types/Wallet";

interface StatusChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (status: TransactionStatus) => void;
  isLoading: boolean;
  currentStatus: string;
  transaction: any;
}

export const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  currentStatus,
  transaction,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<TransactionStatus>(currentStatus as TransactionStatus);

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus as TransactionStatus);
    }
  }, [isOpen, currentStatus]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStatus !== currentStatus) {
      onConfirm(selectedStatus);
    }
  };

  if (!isOpen || !transaction) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "text-green-600 bg-green-50 border-green-200";
      case "CANCELLED":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <RefreshCw className="w-6 h-6 text-blue-600" />
            Changer le statut de la transaction
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Info transaction */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Type :</span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  transaction.type === "RECHARGE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                }`}
              >
                {transaction.type === "RECHARGE" ? (
                  <ArrowUpCircle className="w-3 h-3" />
                ) : (
                  <ArrowDownCircle className="w-3 h-3" />
                )}
                {transaction.type === "RECHARGE" ? "Recharge" : "Débit"}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Montant :</span>
              <span
                className={`font-bold text-lg ${transaction.type === "RECHARGE" ? "text-green-600" : "text-red-600"}`}
              >
                {transaction.type === "RECHARGE" ? "+" : "-"}
                {transaction.amount.toLocaleString()} {transaction.country?.devise}
              </span>
            </div>

            <div className="flex justify-between items-start">
              <span className="text-sm text-gray-600">Utilisateur :</span>
              <div className="text-right">
                <span className="text-sm font-medium block">
                  {transaction.user?.first_name} {transaction.user?.last_name}
                </span>
                <span className="text-xs text-gray-500 block">{transaction.user?.email}</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Pays :</span>
              <span className="text-sm font-medium">
                {transaction.country?.name} ({transaction.country?.code})
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Date :</span>
              <span className="text-xs text-gray-500">
                {transaction.created_at ? new Date(transaction.created_at).toLocaleString("fr-FR") : "—"}
              </span>
            </div>

            {transaction.description && (
              <div className="flex justify-between items-start">
                <span className="text-sm text-gray-600">Description :</span>
                <span className="text-sm text-gray-700 max-w-[200px] text-right break-words">
                  {transaction.description}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-700">Statut actuel :</span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  currentStatus
                )}`}
              >
                {currentStatus === "COMPLETED" && <CheckCircle className="w-3 h-3" />}
                {currentStatus === "CANCELLED" && <Ban className="w-3 h-3" />}
                {currentStatus === "PENDING" && <Loader2 className="w-3 h-3" />}
                {currentStatus === "COMPLETED" && "Complété"}
                {currentStatus === "CANCELLED" && "Annulé"}
                {currentStatus === "PENDING" && "En attente"}
              </span>
            </div>
          </div>

          {/* Nouveau statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Nouveau statut <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedStatus === "COMPLETED" ? "border-green-500 bg-green-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  value="COMPLETED"
                  checked={selectedStatus === "COMPLETED"}
                  onChange={(e) => setSelectedStatus(e.target.value as TransactionStatus)}
                  className="w-4 h-4 text-green-600 flex-shrink-0"
                  disabled={isLoading}
                />
                <div className="flex-1">
                  <span className="font-medium text-green-700">Complété</span>
                  <p className="text-xs text-gray-500 mt-0.5">Marquer la transaction comme terminée avec succès</p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedStatus === "PENDING" ? "border-yellow-500 bg-yellow-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  value="PENDING"
                  checked={selectedStatus === "PENDING"}
                  onChange={(e) => setSelectedStatus(e.target.value as TransactionStatus)}
                  className="w-4 h-4 text-yellow-600 flex-shrink-0"
                  disabled={isLoading}
                />
                <div className="flex-1">
                  <span className="font-medium text-yellow-700">En attente</span>
                  <p className="text-xs text-gray-500 mt-0.5">Mettre la transaction en attente de traitement</p>
                </div>
                <Loader2 className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              </label>

              <label
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedStatus === "CANCELLED" ? "border-red-500 bg-red-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  value="CANCELLED"
                  checked={selectedStatus === "CANCELLED"}
                  onChange={(e) => setSelectedStatus(e.target.value as TransactionStatus)}
                  className="w-4 h-4 text-red-600 flex-shrink-0"
                  disabled={isLoading}
                />
                <div className="flex-1">
                  <span className="font-medium text-red-700">Annulé</span>
                  <p className="text-xs text-gray-500 mt-0.5">Annuler la transaction</p>
                </div>
                <Ban className="w-5 h-5 text-red-600 flex-shrink-0" />
              </label>
            </div>
          </div>

          {/* Avertissement pour les transactions annulées */}
          {selectedStatus === "CANCELLED" && currentStatus !== "CANCELLED" && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700 flex items-start gap-2">
                <Ban className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Attention : L'annulation d'une transaction n'affecte pas automatiquement le solde du wallet. Vous
                  devrez peut-être ajuster manuellement le solde si nécessaire.
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Footer - Fixed avec boutons */}
        <div className="flex space-x-3 p-6 pt-4 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={selectedStatus === currentStatus || isLoading}
            className={`flex-1 text-white px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 font-medium ${
              selectedStatus === "CANCELLED"
                ? "bg-red-600 hover:bg-red-700"
                : selectedStatus === "COMPLETED"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Changement...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Changer le statut</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
